import { useCallback, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isNumber } from 'lodash'
import { selectorPreviewClipCreator, selectInOutPointsTimeLineAssetID } from 'selectors/preview'
import { getReferenceVideoAsset, getTransitionsByAssetId, selectAssetById, selectTimelineDuration } from 'selectors'
import { SliderTimeContext } from 'contexts/SliderTimeContext'
import { PLAYER_TYPE } from 'enums'
import { getFPSByRefVideo, timelineTimeToSeconds } from 'Util'
import { resetMarkers, setPreviewMarker, setPreviewMarkers } from 'actions/preview'
import { stopPlaybackTimeline } from 'actions/playback'
import {
  isMarkerBlockedByDissolve,
  MarkerCommandMarkType,
  resolvePlainMarkerCommand
} from 'helpers/preview/markerCommands'

export const useTimelineMarkerCommands = () => {
  const dispatch = useDispatch()
  const { markIn, markOut } = useSelector(selectorPreviewClipCreator).timeline
  const timelineDuration = useSelector(selectTimelineDuration)
  const inOutPointsTimeLineAssetId = useSelector(selectInOutPointsTimeLineAssetID)
  const inOutPointsTimeLineAsset = useSelector(
    state => selectAssetById(state, inOutPointsTimeLineAssetId)
  )
  const inOutPointsAssetTransitions = useSelector(
    state => getTransitionsByAssetId(state, inOutPointsTimeLineAssetId)
  )
  const refVideo = useSelector(getReferenceVideoAsset)
  const { localTimeRef } = useContext(SliderTimeContext)

  const inOutPointsAssetTransDuration = inOutPointsAssetTransitions.reduce((sum, tr) => {
    // eslint-disable-next-line no-param-reassign
    sum += tr.duration
    return sum
  }, 0)
  const minClipDuration = timelineTimeToSeconds(inOutPointsAssetTransDuration) || 1 / getFPSByRefVideo(refVideo)
  const inOutPointsAssetDissolve = inOutPointsAssetTransitions.find(tr => tr.isMountBetween)
  const duration = timelineTimeToSeconds(timelineDuration)

  const onSetMarker = useCallback((
    markType: MarkerCommandMarkType,
    point?: number
  ) => {
    dispatch(stopPlaybackTimeline())

    if (isMarkerBlockedByDissolve({
      dissolve: inOutPointsAssetDissolve,
      assetId: inOutPointsTimeLineAsset?.id ?? null,
      markType,
    })) return

    const newPoint = isNumber(point) ? point : timelineTimeToSeconds(localTimeRef.current)
    const command = resolvePlainMarkerCommand({
      markType,
      point: newPoint,
      markers: { markIn, markOut },
      duration,
      minClipDuration,
    })

    if (command.kind === 'single') {
      dispatch(setPreviewMarker({
        preview: PLAYER_TYPE.TIMELINE,
        point: command.point,
        markType,
      }))
    } else {
      dispatch(setPreviewMarkers(
        { [PLAYER_TYPE.TIMELINE]: command.markers },
        { preview: PLAYER_TYPE.TIMELINE }
      ))
    }
  }, [
    dispatch,
    inOutPointsAssetDissolve,
    inOutPointsTimeLineAsset,
    markIn,
    markOut,
    duration,
    minClipDuration,
    localTimeRef,
  ])

  const onClearInOutPoints = useCallback(() => {
    dispatch(resetMarkers({ preview: PLAYER_TYPE.TIMELINE }))
  }, [ dispatch ])

  return {
    onSetMarker,
    onClearInOutPoints,
  }
}
